import { DataGridWithFilter } from "components/DataGrid";
import { DataGridCellValue, DataGridColumnConfig, DataGridRowConfig } from "components/DataGrid/types";
import { ApprovedEquipmentSearchResult } from "./useApprovedEquipmentSearch";
import { DataGridHeader } from "components/DataGrid/DataGridHeader";
import { DataGridBody } from "components/DataGrid/DataGridBody";
import { getColumnSortState, getVisibleColumns } from "components/DataGrid/utils";
import { Button } from "components/Button";
import { DataGridFilter } from "components/DataGrid/DataGridFilter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataGridHead } from "components/DataGrid/DataGridHead";
import useMediaQuery from "components/utils/useMediaQuery";
import { DataGridCellBadge } from "components/DataGrid/components/DataGridCellBadge";

export const ApprovedEquipmentGrid: React.FC<{
    gridConfig: any;
    items: ApprovedEquipmentSearchResult[];
    onSelect: (item: ApprovedEquipmentSearchResult) => void;
    sortBy: string;
    sortAsc: boolean;
    totalRecords?: number;
    onSortChange: (key: string) => void;
    onFilterChange: (filter: string) => void;
    filter: string;
    isLoadingGrid: boolean;
    setInitialItemsFound: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
    items,
    gridConfig,
    onSelect,
    sortBy,
    sortAsc,
    totalRecords,
    onSortChange,
    onFilterChange,
    filter,
    isLoadingGrid,
    setInitialItemsFound,
}) => {
    const columns = getVisibleColumns(gridConfig);
    const isMobile = useMediaQuery("(max-width: 768px)");
    const mobileConfig = {
        title: columns.find((i) => ["model_name", "tradebrand_name"].includes(i.columnname))?.key ?? "",
        subtitle: columns.find((i) => i.columnname === "manf")?.key ?? "",
        columns: ["refid"],
    };

    const rows: DataGridRowConfig[] = (items ?? []).map((item, index) => ({
        index,
        data: item,
        actions: [
            {
                content: () => (
                    <Button
                        variant={!isMobile ? "link" : "outline-secondary"}
                        className="text-decoration-none d-flex align-items-center gap-2"
                        onClick={() => onSelect(item)}
                        aria-label="Select equipment"
                    >
                        {isMobile && <span>Select equipment </span>}
                        <FontAwesomeIcon icon={["far", "arrow-right"]} />
                    </Button>
                ),
            },
        ],
        mobileConfig,
    }));

    if (!isLoadingGrid) {
        setInitialItemsFound(true);
    }

    const ColumnBadge = (column: DataGridColumnConfig, _: DataGridCellValue, row: DataGridRowConfig) => {
        return <DataGridCellBadge item={row.data[column.key] ?? ""} style={{ backgroundColor: "rgb(232, 232, 240)" }} />;
    };

    const getColumnRenderer = (column: DataGridColumnConfig) => {
        const mapper: Record<string, typeof ColumnBadge> = {
            refid: ColumnBadge,
        };

        return mapper[column.columnname];
    };

    const formattedColumns: DataGridColumnConfig[] = columns.flatMap((col) => {
        const renderer = getColumnRenderer(col);

        const bodyColumns = mobileConfig.columns.map((i) => i.toLowerCase());
        const headerColumns = [mobileConfig.title, mobileConfig.subtitle].map((i) => i.toLowerCase());
        const bodyColumnsIncludes = bodyColumns.includes(col.columnname.toLowerCase());
        const headerColumnsIncludes = headerColumns.includes(col.key.toLowerCase() as any);
        if (isMobile && !bodyColumnsIncludes && !headerColumnsIncludes) {
            return [];
        }

        return [
            {
                ...col,
                sort: getColumnSortState(col, sortBy, sortAsc),
                name: col.name.split("_").join(" "),
                renderer,
            },
        ];
    });

    return (
        <>
            <DataGridWithFilter
                onFilterChange={onFilterChange}
                onSortChange={onSortChange}
                gridConfig={gridConfig}
                filter={filter}
                className="approved-equipment-list data-grid-in-modal align-middle"
                withWrapper={false}
            >
                {({
                    filter,
                    isFilterOpen,
                    isSortOpen,
                    isFilterChanged,
                    isFilterEmpty,
                    toggleFilter,
                    toggleSort,
                    getFilterValue,
                    filterSelected,
                    onFilterValueChange,
                    onFilterValueKeyDown,
                    onFieldClean,
                    onFilterSubmit,
                    onFilterClear,
                    onSortClear,
                    onFilterInputFocus,
                    setFilterInputRef,
                }) => (
                    <>
                        <DataGridHead className="position-sticky sticky-top">
                            <DataGridHeader
                                toggleFilter={toggleFilter}
                                toggleSort={toggleSort}
                                isFilterOpen={isFilterOpen}
                                isSortOpen={isSortOpen}
                                isFilterEmpty={isFilterEmpty}
                                filter={filter}
                                sortBy={sortBy}
                                sortAsc={sortAsc}
                                columns={formattedColumns}
                                totalRecords={totalRecords}
                                onSortChange={onSortChange}
                                onFilterClear={onFilterClear}
                                onSortClear={onSortClear}
                                isFilterChanged={isFilterChanged}
                                getFilterValue={getFilterValue}
                                onFieldClean={onFieldClean}
                                onFilterSubmit={onFilterSubmit}
                                onFilterInputFocus={onFilterInputFocus}
                            />
                            <DataGridFilter
                                setFilterInputRef={setFilterInputRef}
                                toggleFilter={toggleFilter}
                                toggleSort={toggleSort}
                                isFilterOpen={isFilterOpen}
                                isSortOpen={isSortOpen}
                                columns={formattedColumns}
                                filter={filter}
                                sortBy={sortBy}
                                sortAsc={sortAsc}
                                filterSelected={filterSelected}
                                onFilterChange={onFilterChange}
                                getFilterValue={getFilterValue}
                                onFilterValueChange={onFilterValueChange}
                                onFilterValueKeyDown={onFilterValueKeyDown}
                                onFilterClear={onFilterClear}
                                onSortClear={onSortClear}
                                onFieldClean={onFieldClean}
                                onFilterSubmit={onFilterSubmit}
                                onFilterInputFocus={onFilterInputFocus}
                                onSortChange={onSortChange}
                                isFilterEmpty={isFilterEmpty}
                            />
                        </DataGridHead>
                        <DataGridBody
                            rows={rows}
                            columns={formattedColumns}
                            isLoadingData={isLoadingGrid}
                            isFilterEmpty={isFilterEmpty}
                            onFilterClear={onFilterClear}
                        />
                    </>
                )}
            </DataGridWithFilter>
        </>
    );
};
